// frontend/src/lib/leaveGuard.tsx
// A single interception point for "the user is trying to leave a live
// argument". The Argument screen arms the guard while a game is in flight;
// every chrome-level link asks the guard first and lets it swallow the click.
//
// Why a context rather than react-router's useBlocker: the app mounts a plain
// <BrowserRouter> (see main.tsx), and useBlocker requires a data router
// (createBrowserRouter). Converting the whole router to get one blocker is a
// much bigger change than a 40-line context, and this way the guard also
// covers the browser back button, which is wired up by the caller.
import { createContext, forwardRef, useCallback, useContext, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';

/**
 * Asked on every guarded navigation. Return `true` to swallow it (the guard
 * has taken over, e.g. by opening a confirmation modal); `false` to let it
 * through.
 */
export type LeaveAttempt = (to: string, label: string) => boolean;

interface LeaveGuardValue {
  /** Arm the guard with a handler, or disarm it with `null`. */
  setGuard: (fn: LeaveAttempt | null) => void;
  /** Ask the armed guard about a navigation. `true` = intercepted. */
  attempt: LeaveAttempt;
}

const noop: LeaveGuardValue = { setGuard: () => {}, attempt: () => false };

const LeaveGuardContext = createContext<LeaveGuardValue>(noop);

export function LeaveGuardProvider({ children }: { children: ReactNode }) {
  // A ref, not state: arming/disarming the guard must not re-render the whole
  // chrome (the header re-renders on every XP tick as it is).
  const guardRef = useRef<LeaveAttempt | null>(null);

  const setGuard = useCallback((fn: LeaveAttempt | null) => {
    guardRef.current = fn;
  }, []);

  const attempt = useCallback<LeaveAttempt>((to, label) => {
    const fn = guardRef.current;
    return fn ? fn(to, label) : false;
  }, []);

  const value = useMemo(() => ({ setGuard, attempt }), [setGuard, attempt]);

  return <LeaveGuardContext.Provider value={value}>{children}</LeaveGuardContext.Provider>;
}

export function useLeaveGuard(): LeaveGuardValue {
  return useContext(LeaveGuardContext);
}

interface GuardedLinkProps extends LinkProps {
  /** Human name for the destination, shown in the confirmation copy. */
  guardLabel: string;
}

/**
 * A `<Link>` that runs past the leave guard first. Modifier-clicks and
 * middle-clicks (open-in-new-tab) are deliberately NOT intercepted: they don't
 * take the user away from the argument, so prompting on them would be noise.
 *
 * forwardRef because the header's settings menu keeps an anchor ref per item
 * for roving-focus keyboard navigation.
 */
export const GuardedLink = forwardRef<HTMLAnchorElement, GuardedLinkProps>(
  function GuardedLink({ guardLabel, onClick, to, ...rest }, ref) {
    const { attempt } = useLeaveGuard();
    return (
      <Link
        ref={ref}
        to={to}
        {...rest}
        onClick={(e) => {
          onClick?.(e);
          if (e.defaultPrevented) return;
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          if (typeof to !== 'string') return;
          if (attempt(to, guardLabel)) e.preventDefault();
        }}
      />
    );
  },
);
